import axios from './axiosConfig'

import { getEnv } from '@utils/env'
import { getClaims } from './claims.api'
import { getTrackingReferenceValues } from '@utils/tracking.util'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

const EXTERNAL_LAYER_API = getEnv('REACT_APP_EXTERNAL_LAYER_CLAIMS', '')
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_CLAIMS', '')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type CreateLinkTokenResponseData = {
  link_token: string
}

type ExchangePublicTokenForAccessTokenResponseData = {
  institution_name: string
}

type CheckLinkStatusResponseData = {
  link_status: string
  institution_name: string
}

export type CreateLinkTokenResponse = ApiResponse<CreateLinkTokenResponseData>
export type ExchangePublicTokenForAccessTokenResponse =
  ApiResponse<ExchangePublicTokenForAccessTokenResponseData>
export type CheckLinkStatusResponse = ApiResponse<CheckLinkStatusResponseData>

export const createLinkToken = async (): Promise<CreateLinkTokenResponse> => {
  try {
    const { db_name, user_id } = getTrackingReferenceValues()
    const response = await axios.post<CreateLinkTokenResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/tracking/create-link-token`,
      { db_name, user_id },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createLinkToken:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const exchangePublicTokenForAccessToken = async (
  public_token: string,
): Promise<ExchangePublicTokenForAccessTokenResponse> => {
  try {
    const { clinic_id, db_name, user_id } = getTrackingReferenceValues()
    const currentClaims = await getClaims(undefined as unknown as string)
    const response =
      await axios.post<ExchangePublicTokenForAccessTokenResponse>(
        `${EXTERNAL_LAYER_API}/api/v1/external/tracking/exchange-public-token`,
        {
          public_token,
          clinic_id,
          db_name,
          user_id,
          claims: currentClaims.data.claims,
        },
        {
          headers,
        },
      )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in exchangePublicTokenForAccessToken:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getLinkStatus = async (): Promise<CheckLinkStatusResponse> => {
  try {
    const { clinic_id } = getTrackingReferenceValues()
    const response = await axios.get<CheckLinkStatusResponse>(
      `${BUSINESS_LAYER_API}/api/v1/tracking/link-status/${clinic_id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in checkLinkStatus:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const unlinkClinic = async (): Promise<CheckLinkStatusResponse> => {
  try {
    const { clinic_id, db_name, user_id } = getTrackingReferenceValues()
    const response = await axios.post<CheckLinkStatusResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/tracking/unlink`,
      {
        clinic_id,
        db_name,
        user_id,
      },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in checkLinkStatus:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getClaimsByPaymentReferenceNumber = async (
  payment_reference_number: string,
): Promise<any> => {
  try {
    const response = await axios.get<any>(
      `${BUSINESS_LAYER_API}/api/v1/tracking/find-claims/${payment_reference_number}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getClaimsByPaymentReferenceNumber:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const postTransaction = async (id: string): Promise<any> => {
  try {
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/tracking/post-transaction/${id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in postTransaction:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const postAllTransactions = async (): Promise<any> => {
  try {
    const { clinic_id, user_id } = getTrackingReferenceValues()
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/tracking/post-all-transactions`,
      {
        clinic_id,
        user_id,
      },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in postAllTransactions:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const addNewTrackingTransaction = async (
  transaction: any,
): Promise<any> => {
  try {
    const { clinic_id, user_id } = getTrackingReferenceValues()
    transaction.clinic_id = clinic_id
    transaction.user_id = user_id
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/tracking/add-transaction`,
      transaction,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in addNewTrackingTransaction:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteTrackingTransaction = async (id: string): Promise<any> => {
  try {
    const response = await axios.delete<any>(
      `${BUSINESS_LAYER_API}/api/v1/tracking/delete-transaction/${id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteTrackingTransaction:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const checkTransactionIfExists = async (
  field: string,
  value: string,
  forSimulator: boolean = false,
): Promise<any> => {
  try {
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/tracking/transaction-exists`,
      { field, value, forSimulator },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in checkTransactionIfExists:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const testWebhook = async (data?: any): Promise<any> => {
  try {
    const { clinic_id, db_name, user_id } = getTrackingReferenceValues()
    let claimsData: any[] = []
    if (data) {
      const currentClaims = await getClaims(undefined as unknown as string)
      const updatedclaim = currentClaims.data.claims.find(
        (claim: any) => claim.public_id === data.claim_public_id,
      )
      claimsData = [
        {
          ...updatedclaim,
          payment_method: data.payment_method,
          reference_number: data.reference_number,
          check_status: data.check_status,
        },
      ]
    }
    const response = await axios.post<any>(
      `${EXTERNAL_LAYER_API}/api/v1/external/tracking/fire-test-webhook`,
      {
        clinic_id,
        db_name,
        user_id,
        ...(data ? { claims_data: claimsData } : {}),
      },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in testWebhook:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
